import {map} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {outgoingMessagesApi, userApi} from 'api';
import {
    DatePicker, FiltersDropdown, FiltersWrapper, FiltersWrapperChildrenProps, Input, PageHeader, Pagination, Select, Tab, Tabs
} from 'components';
import {UserContext} from 'context';
import {useApiData, useObservable} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {authDataStore} from 'services';
import {
    OutgoingMessageChannel, OutgoingMessageFeature, OutgoingMessageFilters, OutgoingMessageStatus,
    OutgoingMessageStatusDetails, PaginationOptions, UserSummary
} from 'types';
import {buildTranslationKey, userAuthUtils} from 'utils';

import {DownloadMessagesButton} from './DownloadMessagesButton';
import {OutgoingMessagesList} from './OutgoingMessagesList';

const defaultPagination: PaginationOptions = {
    page: 1,
    pages: 1,
    count: 0,
    pageSize: 10
};

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.OUTGOING_MESSAGES;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const CLIENTS_CONNECTIONS_T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS.SEND_MESSAGE_DIALOG; // TODO: make common

export const OutgoingMessages: React.FC = () => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);
    const {send: loadUsers, data: users} = useApiData<UserSummary[]>(userApi.getUsersSummary);

    const {active, history} = useMemo(() =>
        userAuthUtils.getUserPermissions().account.outgoingMessages, []);
    const [selectedTabName, setSelectedTabName] = useState<string>(active?.enabled ? t(T_KEYS.TABS.ACTIVE) : t(T_KEYS.TABS.HISTORY));
    const isHistoryTab = useMemo(() => selectedTabName === t(T_KEYS.TABS.HISTORY), [selectedTabName, t]);

    const {localUser} = useObservable(authDataStore);
    const canManage = useMemo(() => userAuthUtils.isAppAdmin(localUser), [localUser]);

    const [filters, setFilters] = useState<OutgoingMessageFilters>({});
    const [pagination, setPagination] = useState<PaginationOptions>(defaultPagination);
    const [itemsCount, setItemsCount] = useState<number>(0);

    const statusFilterOptions = useMemo(() => map([OutgoingMessageStatus.Sent, OutgoingMessageStatus.Error], (status) => ({
        id: status,
        name: t(T_KEYS_COMMON.OUTGOING_MESSAGE_STATUS[buildTranslationKey(status)])
    })), [t]);

    const statusDetailsFilterOptions = useMemo(() => map(OutgoingMessageStatusDetails, (statusDetails) => ({
        id: statusDetails,
        name: t(CLIENTS_CONNECTIONS_T_KEYS.ERRORS[buildTranslationKey(statusDetails)])
    })), [t]);

    const channelFilterOptions = useMemo(() => map(OutgoingMessageChannel, (channel) => ({
        id: channel,
        name: t(T_KEYS_COMMON.OUTGOING_MESSAGE_CHANNEL[buildTranslationKey(channel)])
    })), [t]);

    const featureFilterOptions = useMemo(() => map(OutgoingMessageFeature, (feature) => ({
        id: feature,
        name: t(T_KEYS_COMMON.OUTGOING_MESSAGE_FEATURE[buildTranslationKey(feature)])
    })), [t]);

    const getActiveMessages = useCallback(() =>
        outgoingMessagesApi.getActiveMessages(filters, pagination), [filters, pagination]);

    const getHistoryMessages = useCallback(() =>
        outgoingMessagesApi.getHistoryMessages(filters, pagination), [filters, pagination]);

    const removeActiveMessage = useCallback((id: string) =>
        outgoingMessagesApi.removeActiveMessage(id), []);

    const removeHistoryMessage = useCallback((id: string) =>
        outgoingMessagesApi.removeHistoryMessage(id), []);

    const setPage = useCallback((value: number) => {
        setPagination((prev) => ({...prev, page: value}));
    }, []);

    const setPageSize = useCallback((value: number) => {
        setPagination((prev) => ({...prev, pageSize: value}));
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    return (
        <Fragment>
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />
            <div className='tw-flex tw-justify-between tw-mb-2 tw-flex-wrap'>
                <div className='tw-flex'>
                    <FiltersWrapper
                        defaultFilters={{}}
                        filters={filters}
                        onSubmit={setFilters}
                    >
                        {({filters: _filters, onFiltersChange, ...rest}: FiltersWrapperChildrenProps<OutgoingMessageFilters>) => (
                            <FiltersDropdown
                                className='tw-flex-nowrap tw-my-2'
                                {...rest}
                            >
                                <Fragment>
                                    <DatePicker
                                        maxDate={_filters.createdBeforeDate}
                                        value={_filters.createdAfterDate}
                                        timeZone={timeZone}
                                        format={dateFormat}
                                        label={t(T_KEYS.FILTERS.CREATED_AFTER_DATE)}
                                        onChange={(value) => onFiltersChange({createdAfterDate: value})}
                                    />
                                    <DatePicker
                                        minDate={_filters.createdAfterDate}
                                        value={_filters.createdBeforeDate}
                                        timeZone={timeZone}
                                        format={dateFormat}
                                        label={t(T_KEYS.FILTERS.CREATED_BEFORE_DATE)}
                                        onChange={(value) => onFiltersChange({createdBeforeDate: value})}
                                    />
                                    <Input
                                        name='recipient'
                                        value={_filters.recipient}
                                        label={t(T_KEYS.FILTERS.RECIPIENT)}
                                        onChange={(value) => onFiltersChange({recipient: value})}
                                    />
                                    <Select
                                        value={_filters.channel}
                                        noneOption
                                        valueAs='id'
                                        placeholder={t(T_KEYS.FILTERS.CHANNEL)}
                                        options={channelFilterOptions}
                                        onChange={(value) => onFiltersChange({channel: value})}
                                    />
                                    <Select
                                        value={_filters.feature}
                                        noneOption
                                        valueAs='id'
                                        placeholder={t(T_KEYS.FILTERS.FEATURE)}
                                        options={featureFilterOptions}
                                        onChange={(value) => onFiltersChange({feature: value})}
                                    />
                                    <Input
                                        name='clientConnectId'
                                        value={_filters.clientConnectId}
                                        label={t(T_KEYS.FILTERS.CLIENT_CONNECT_ID)}
                                        onChange={(value) => onFiltersChange({clientConnectId: value})}
                                    />
                                    {isHistoryTab &&
                                        <Fragment>
                                            <Select
                                                value={_filters.status}
                                                noneOption
                                                valueAs='id'
                                                placeholder={t(T_KEYS.FILTERS.STATUS)}
                                                options={statusFilterOptions}
                                                onChange={(value) => onFiltersChange({status: value})}
                                            />
                                            <Select
                                                value={_filters.statusDetails}
                                                noneOption
                                                valueAs='id'
                                                placeholder={t(T_KEYS.FILTERS.STATUS_DETAILS)}
                                                options={statusDetailsFilterOptions}
                                                className='tw-max-w-64'
                                                onChange={(value) => onFiltersChange({statusDetails: value})}
                                            />
                                        </Fragment>}
                                </Fragment>
                            </FiltersDropdown>
                        )}
                    </FiltersWrapper>
                    {isHistoryTab && itemsCount > 0 &&
                        <div className='tw-flex tw-items-center'>
                            <DownloadMessagesButton filters={filters} />
                        </div>}
                </div>

                {itemsCount > 0 && (
                    <Pagination
                        maxItemsToDisplay={9}
                        itemsClassName='tw-min-w-64 tw-justify-between md:tw-justify-end'
                        rangeClassName='-sm:tw-hidden'
                        pageSelectorClassName='-sm:tw-hidden'
                        pageSize={pagination.pageSize}
                        pageNumber={pagination.page}
                        totalItems={itemsCount}
                        onPageNumberChange={setPage}
                        onPageSizeChange={setPageSize}
                    />)}
            </div>

            <Tabs className='scrollbar-hide' onChange={({name}) => setSelectedTabName(name)}>
                {active?.enabled &&
                    <Tab name={t(T_KEYS.TABS.ACTIVE)}>
                        <OutgoingMessagesList
                            getFn={getActiveMessages}
                            removeFn={removeActiveMessage}
                            users={users}
                            canManage={canManage}
                            onItemsCountUpdate={setItemsCount}
                        />
                    </Tab>}
                {history?.enabled &&
                    <Tab name={t(T_KEYS.TABS.HISTORY)}>
                        <OutgoingMessagesList
                            getFn={getHistoryMessages}
                            removeFn={removeHistoryMessage}
                            users={users}
                            isHistory
                            canManage={canManage}
                            onItemsCountUpdate={setItemsCount}
                        />
                    </Tab>}
            </Tabs>
        </Fragment>
    );
};